import React, { useContext, useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { AuthContext } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { 
  ChevronLeft, Mic, MicOff, Volume2, Play, Pause,
  CheckCircle, XCircle, RotateCcw, Loader2, Sparkles,
  Trophy, Star, Target, Headphones
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// PA Announcements for voice practice - EXPANDED CONTENT
const VOICE_EXERCISES = [
  {
    id: 'welcome',
    category: 'Hoş Geldiniz Anonsu',
    icon: '👋',
    color: 'from-blue-500 to-indigo-600',
    exercises: [
      {
        id: 'welcome_1',
        title: 'Temel Karşılama',
        text: "Good morning, ladies and gentlemen. Welcome aboard Turkish Airlines flight 1234 to Istanbul.",
        text_tr: "Günaydın, bayanlar ve baylar. Turkish Airlines 1234 sefer sayılı İstanbul uçuşumuza hoş geldiniz.",
        difficulty: 'easy',
        tips: ["Speak clearly and slowly", "Smile while speaking", "Emphasize 'Welcome'"]
      },
      {
        id: 'welcome_2',
        title: 'Tam Karşılama',
        text: "Good afternoon, ladies and gentlemen. On behalf of Captain Smith and the entire crew, welcome aboard flight 789 to London Heathrow. Our flight time today will be approximately three hours.",
        text_tr: "İyi günler, bayanlar ve baylar. Kaptan Smith ve tüm ekip adına, 789 sefer sayılı Londra Heathrow uçuşumuza hoş geldiniz. Bugünkü uçuş süremiz yaklaşık üç saat olacaktır.",
        difficulty: 'medium',
        tips: ["Take natural pauses", "Be warm and professional", "Pronounce 'Heathrow' correctly"]
      },
      {
        id: 'welcome_3',
        title: 'Premium Sınıf Karşılama',
        text: "Good evening and welcome to Business Class. My name is Sarah, and I will be your cabin manager today. Please make yourself comfortable, and do not hesitate to call us if you need anything.",
        text_tr: "İyi akşamlar ve İş Sınıfı'na hoş geldiniz. Ben Sarah, bugün kabin şefiniz olacağım. Lütfen rahat edin ve herhangi bir şeye ihtiyacınız olursa bizi aramaktan çekinmeyin.",
        difficulty: 'medium',
        tips: ["Personalize the greeting", "Sound warm and exclusive", "Make eye contact"]
      },
      {
        id: 'welcome_4',
        title: 'Uluslararası Uçuş',
        text: "Ladies and gentlemen, welcome aboard Emirates flight 202 to Dubai. We are pleased to have you with us today. Our flying time will be approximately four hours and thirty minutes. Please sit back, relax, and enjoy the flight.",
        text_tr: "Bayanlar ve baylar, Emirates 202 sefer sayılı Dubai uçuşumuza hoş geldiniz. Bugün sizleri aramızda görmekten mutluluk duyuyoruz. Uçuş süremiz yaklaşık dört saat otuz dakika olacaktır. Lütfen arkanıza yaslanın, rahatlayın ve uçuşun tadını çıkarın.",
        difficulty: 'hard',
        tips: ["Maintain professional tone", "Pronounce Emirates correctly", "Project confidence"]
      },
      {
        id: 'welcome_5',
        title: 'Bağlantılı Uçuş',
        text: "For passengers connecting to other flights, our ground staff will be available at the arrival gate to assist you with your connections. Please check the monitors for your connecting gate information.",
        text_tr: "Diğer uçuşlara bağlantı yapacak yolcularımız için, yer ekibimiz varış kapısında bağlantılarınız konusunda size yardımcı olmak üzere hazır olacaktır. Lütfen bağlantı kapı bilgilerinizi monitörlerden kontrol edin.",
        difficulty: 'medium',
        tips: ["Speak clearly", "Emphasize key information", "Be helpful"]
      }
    ]
  },
  {
    id: 'safety',
    category: 'Güvenlik Anonsu',
    icon: '🔒',
    color: 'from-red-500 to-orange-600',
    exercises: [
      {
        id: 'safety_1',
        title: 'Emniyet Kemeri',
        text: "Ladies and gentlemen, the captain has turned on the fasten seatbelt sign. Please return to your seats and fasten your seatbelts.",
        text_tr: "Bayanlar ve baylar, kaptan emniyet kemeri işaretini yaktı. Lütfen koltuklarınıza dönün ve emniyet kemerlerinizi bağlayın.",
        difficulty: 'easy',
        tips: ["Sound calm but authoritative", "Clear pronunciation of 'fasten'"]
      },
      {
        id: 'safety_2',
        title: 'Acil Çıkış',
        text: "Please take a moment to locate your nearest emergency exit. Remember, the closest exit may be behind you. In the unlikely event of an emergency landing, leave all personal belongings behind.",
        text_tr: "Lütfen en yakın acil çıkışınızı bulun. Unutmayın, en yakın çıkış arkanızda olabilir. Beklenmedik bir acil iniş durumunda, tüm kişisel eşyalarınızı geride bırakın.",
        difficulty: 'medium',
        tips: ["Emphasize 'emergency exit'", "Pause between sentences", "Sound reassuring"]
      },
      {
        id: 'safety_3',
        title: 'Oksijen Maskesi',
        text: "In the event of a loss of cabin pressure, oxygen masks will drop from the panel above your head. Pull the mask towards you and place it over your nose and mouth. Secure your own mask before assisting others.",
        text_tr: "Kabin basıncı düşmesi durumunda, oksijen maskeleri başınızın üzerindeki panelden inecektir. Maskeyi kendinize doğru çekin ve burnunuzu ve ağzınızı kapatacak şekilde yerleştirin. Başkalarına yardım etmeden önce kendi maskenizi takın.",
        difficulty: 'hard',
        tips: ["Clear and calm delivery", "Demonstrate while speaking", "Emphasize 'before assisting others'"]
      },
      {
        id: 'safety_4',
        title: 'Can Yeleği',
        text: "Your life vest is located in a pouch under your seat or between the armrests. To put on the vest, slip it over your head and fasten the straps at your waist. Do not inflate the vest until you leave the aircraft.",
        text_tr: "Can yeleğiniz koltuğunuzun altında veya kol dayanakları arasındaki çantada bulunmaktadır. Yeleği giymek için başınızdan geçirin ve bel kayışlarını bağlayın. Uçaktan çıkana kadar yeleği şişirmeyin.",
        difficulty: 'hard',
        tips: ["Step by step delivery", "Clear pronunciation", "Emphasize 'do not inflate'"]
      },
      {
        id: 'safety_5',
        title: 'Elektronik Cihazlar',
        text: "Please ensure all electronic devices are switched to airplane mode. Larger devices such as laptops should be stowed in the overhead bin or under the seat in front of you during takeoff and landing.",
        text_tr: "Lütfen tüm elektronik cihazlarınızın uçak moduna alındığından emin olun. Dizüstü bilgisayarlar gibi büyük cihazlar kalkış ve iniş sırasında üst bagaj bölmesinde veya önünüzdeki koltuğun altında saklanmalıdır.",
        difficulty: 'medium',
        tips: ["Be polite but firm", "Clear instructions", "Mention both locations"]
      }
    ]
  },
  {
    id: 'turbulence',
    category: 'Türbülans Anonsu',
    icon: '💨',
    color: 'from-purple-500 to-violet-600',
    exercises: [
      {
        id: 'turbulence_1',
        title: 'Hafif Türbülans',
        text: "Ladies and gentlemen, we are experiencing some light turbulence. Please remain seated with your seatbelt fastened.",
        text_tr: "Bayanlar ve baylar, hafif türbülans yaşıyoruz. Lütfen emniyet kemerinizi bağlı tutarak koltuğunuzda kalın.",
        difficulty: 'easy',
        tips: ["Stay calm", "Don't sound alarming", "Clear and steady voice"]
      },
      {
        id: 'turbulence_2',
        title: 'Şiddetli Türbülans',
        text: "Ladies and gentlemen, we are experiencing severe turbulence. For your safety, please return to your seats immediately and fasten your seatbelts securely. Cabin crew, please be seated.",
        text_tr: "Bayanlar ve baylar, şiddetli türbülans yaşıyoruz. Güvenliğiniz için lütfen derhal koltuklarınıza dönün ve emniyet kemerlerinizi sıkıca bağlayın. Kabin ekibi, lütfen oturun.",
        difficulty: 'hard',
        tips: ["Firm but not panicked", "Emphasize 'immediately'", "Command presence"]
      },
      {
        id: 'turbulence_3',
        title: 'Türbülans Sonrası',
        text: "Ladies and gentlemen, we have now passed through the turbulence. The captain has turned off the fasten seatbelt sign. You are now free to move about the cabin. However, we recommend keeping your seatbelt fastened while seated.",
        text_tr: "Bayanlar ve baylar, türbülansı geçtik. Kaptan emniyet kemeri işaretini kapattı. Artık kabin içinde serbestçe dolaşabilirsiniz. Ancak, oturduğunuz sırada emniyet kemerinizi bağlı tutmanızı öneririz.",
        difficulty: 'medium',
        tips: ["Sound relieved but professional", "Clear recommendation", "Reassuring tone"]
      },
      {
        id: 'turbulence_4',
        title: 'Beklenmeyen Türbülans',
        text: "Ladies and gentlemen, we apologize for the unexpected turbulence. The situation is now under control. For your safety, we recommend you keep your seatbelt fastened at all times when seated.",
        text_tr: "Bayanlar ve baylar, beklenmeyen türbülans için özür dileriz. Durum şu an kontrol altında. Güvenliğiniz için, oturduğunuz her zaman emniyet kemerinizi bağlı tutmanızı öneririz.",
        difficulty: 'medium',
        tips: ["Apologetic but confident", "Reassure passengers", "Clear safety reminder"]
      }
    ]
  },
  {
    id: 'landing',
    category: 'İniş Anonsu',
    icon: '✈️',
    color: 'from-green-500 to-teal-600',
    exercises: [
      {
        id: 'landing_1',
        title: 'İniş Hazırlığı',
        text: "Ladies and gentlemen, we will be landing shortly. Please make sure your seatbelt is fastened, your seat back is upright, and your tray table is stowed.",
        text_tr: "Bayanlar ve baylar, kısa süre içinde iniş yapacağız. Lütfen emniyet kemerinizin bağlı olduğundan, koltuk arkalığınızın dik konumda olduğundan ve masanızın kapalı olduğundan emin olun.",
        difficulty: 'easy',
        tips: ["Clear checklist delivery", "Pause between items"]
      },
      {
        id: 'landing_2',
        title: 'Varış Bilgisi',
        text: "Ladies and gentlemen, we have begun our descent into Istanbul Airport. The local time is 3:45 PM and the temperature is 22 degrees Celsius. We hope you had a pleasant flight with us today.",
        text_tr: "Bayanlar ve baylar, İstanbul Havalimanı'na alçalmaya başladık. Yerel saat 15:45 ve sıcaklık 22 derece. Bugün bizimle keyifli bir uçuş geçirdiğinizi umuyoruz.",
        difficulty: 'medium',
        tips: ["Warm tone", "Clear numbers", "End positively"]
      },
      {
        id: 'landing_3',
        title: 'Kapı Seçimi',
        text: "We will be arriving at Gate B12. Please remain seated until the aircraft has come to a complete stop and the seatbelt sign has been turned off. Please check around your seat for any personal belongings.",
        text_tr: "B12 kapısına varacağız. Uçak tamamen durana ve emniyet kemeri işareti sönene kadar lütfen yerinizde kalın. Lütfen kişisel eşyalarınız için koltuğunuzun çevresini kontrol edin.",
        difficulty: 'medium',
        tips: ["Clear gate number", "Remind about belongings", "Patient tone"]
      },
      {
        id: 'landing_4',
        title: 'Vedalaşma',
        text: "On behalf of Turkish Airlines and the entire crew, we would like to thank you for flying with us today. We hope to see you again soon on another Turkish Airlines flight. Goodbye and have a wonderful day.",
        text_tr: "Turkish Airlines ve tüm ekip adına, bugün bizimle uçtuğunuz için teşekkür ederiz. Sizi yakında başka bir Turkish Airlines uçuşunda tekrar görmeyi umuyoruz. Hoşça kalın ve güzel bir gün geçirin.",
        difficulty: 'medium',
        tips: ["Warm and sincere", "Company name clear", "Genuine farewell"]
      },
      {
        id: 'landing_5',
        title: 'Bağlantılı Uçuş Hatırlatma',
        text: "For passengers with connecting flights, please check the information screens in the terminal for your gate number. If you need assistance, our ground staff will be happy to help you.",
        text_tr: "Aktarmalı uçuşu olan yolcularımız için, lütfen kapı numaranız için terminaldeki bilgi ekranlarını kontrol edin. Yardıma ihtiyacınız olursa, yer ekibimiz size yardımcı olmaktan mutluluk duyacaktır.",
        difficulty: 'easy',
        tips: ["Helpful tone", "Clear directions", "Offer assistance"]
      }
    ]
  },
  {
    id: 'service',
    category: 'Servis Anonsu',
    icon: '🍽️',
    color: 'from-amber-500 to-yellow-600',
    exercises: [
      {
        id: 'service_1',
        title: 'Yemek Servisi',
        text: "Ladies and gentlemen, we will shortly begin our meal service. Today we are offering chicken with rice or beef with pasta. Please let us know if you have any dietary requirements.",
        text_tr: "Bayanlar ve baylar, kısa süre içinde yemek servisimize başlayacağız. Bugün pilavlı tavuk veya makarnalı et sunuyoruz. Herhangi bir diyet ihtiyacınız varsa lütfen bize bildirin.",
        difficulty: 'medium',
        tips: ["Clear menu options", "Friendly tone", "Mention dietary needs"]
      },
      {
        id: 'service_2',
        title: 'İçecek Servisi',
        text: "Ladies and gentlemen, we will now come through the cabin with our beverage service. We have a selection of soft drinks, juices, tea, coffee, and alcoholic beverages available.",
        text_tr: "Bayanlar ve baylar, şimdi içecek servisimiz ile kabin içinden geçeceğiz. Meşrubatlar, meyve suları, çay, kahve ve alkollü içecekler mevcuttur.",
        difficulty: 'easy',
        tips: ["List options clearly", "Welcoming tone", "Be prepared for questions"]
      },
      {
        id: 'service_3',
        title: 'Duty-Free Satış',
        text: "Ladies and gentlemen, we will shortly be passing through the cabin with our duty-free selection. Please take a look at the catalog in your seat pocket. Cash, credit cards, and multiple currencies are accepted.",
        text_tr: "Bayanlar ve baylar, kısa süre içinde gümrüksüz ürün seçkimizle kabinden geçeceğiz. Lütfen koltuk cebinizdeki kataloğa göz atın. Nakit, kredi kartı ve birden fazla para birimi kabul edilmektedir.",
        difficulty: 'medium',
        tips: ["Promotional tone", "Clear payment options", "Refer to catalog"]
      }
    ]
  },
  {
    id: 'delay',
    category: 'Rötar/Gecikme Anonsu',
    icon: '⏰',
    color: 'from-gray-500 to-slate-600',
    exercises: [
      {
        id: 'delay_1',
        title: 'Kalkış Gecikmesi',
        text: "Ladies and gentlemen, we apologize for the delay in our departure. We are currently waiting for clearance from air traffic control. We expect to be airborne within the next 20 minutes.",
        text_tr: "Bayanlar ve baylar, kalkışımızdaki gecikme için özür dileriz. Şu anda hava trafik kontrolünden izin bekliyoruz. Önümüzdeki 20 dakika içinde havalanmayı bekliyoruz.",
        difficulty: 'medium',
        tips: ["Apologetic tone", "Explain reason", "Give time estimate"]
      },
      {
        id: 'delay_2',
        title: 'Teknik Gecikme',
        text: "Ladies and gentlemen, we regret to inform you that there will be a short delay due to a minor technical issue. Our engineers are working to resolve this as quickly as possible. We appreciate your patience.",
        text_tr: "Bayanlar ve baylar, küçük bir teknik sorun nedeniyle kısa bir gecikme olacağını bildirmekten üzüntü duyuyoruz. Mühendislerimiz bunu en kısa sürede çözmek için çalışıyorlar. Sabrınız için teşekkür ederiz.",
        difficulty: 'hard',
        tips: ["Reassuring tone", "Minimize alarm", "Thank for patience"]
      },
      {
        id: 'delay_3',
        title: 'Hava Koşulları Gecikmesi',
        text: "Ladies and gentlemen, due to adverse weather conditions at our destination, we will be holding in a pattern for approximately 15 minutes. We apologize for any inconvenience this may cause.",
        text_tr: "Bayanlar ve baylar, varış noktamızdaki olumsuz hava koşulları nedeniyle yaklaşık 15 dakika bekleme yapacağız. Bunun neden olabileceği herhangi bir rahatsızlık için özür dileriz.",
        difficulty: 'hard',
        tips: ["Calm and professional", "Clear time estimate", "Acknowledge inconvenience"]
      }
    ]
  }
];

export default function CabinCrewVoicePractice() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [currentExercise, setCurrentExercise] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [recordedAudio, setRecordedAudio] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [loading, setLoading] = useState(false);
  const [completedExercises, setCompletedExercises] = useState([]);
  const [previousRecordings, setPreviousRecordings] = useState([]);
  const [showRecordings, setShowRecordings] = useState(false);
  
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioRef = useRef(null);

  // Load previous recordings
  const loadPreviousRecordings = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/voice-recordings/my-recordings`, { withCredentials: true });
      if (response.data && response.data.recordings) {
        setPreviousRecordings(response.data.recordings);
      }
    } catch (error) {
      console.error("Error loading recordings:", error);
    }
  };

  useEffect(() => {
    loadPreviousRecordings();
  }, []);

  const playbackRef = useRef(null);

  // Demo kullanıcılar için sadece welcome ve landing kategorileri
  const isDemo = user?.is_demo || false;
  const demoAllowedCategories = ['welcome', 'landing'];

  const currentCategory = VOICE_EXERCISES.find(c => c.id === selectedCategory);

  // Play reference audio (TTS)
  const playReferenceAudio = async (text) => {
    if (isPlaying) return;
    
    // First try browser TTS as primary (OpenAI rate limited)
    if ('speechSynthesis' in window) {
      try {
        setIsPlaying(true);
        const utterance = new SpeechSynthesisUtterance(text);
        utterance.lang = 'en-US';
        utterance.rate = 0.9;
        utterance.onend = () => setIsPlaying(false);
        utterance.onerror = () => setIsPlaying(false);
        speechSynthesis.speak(utterance);
        return;
      } catch (e) {
        console.error('Browser TTS failed:', e);
      }
    }
    
    // Fallback to API if browser TTS not available
    try {
      setIsPlaying(true);
      const response = await axios.post(
        `${API_URL}/api/speaking/text-to-speech`,
        { text },
        { responseType: 'blob' }
      );
      
      const contentType = response.headers['content-type'] || '';
      if (contentType.includes('audio')) {
        const audioUrl = URL.createObjectURL(response.data);
        if (audioRef.current) {
          audioRef.current.src = audioUrl;
          audioRef.current.play();
        }
      }
    } catch (error) {
      console.error('TTS API error:', error);
      toast.error('Ses oynatılamadı');
    }
  };

  // Start recording
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];
      
      mediaRecorderRef.current.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };
      
      mediaRecorderRef.current.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/wav' });
        setRecordedAudio(audioBlob);
        stream.getTracks().forEach(track => track.stop());
      };
      
      mediaRecorderRef.current.start();
      setIsRecording(true);
      toast.info('Kayıt başladı... 🎙️');
    } catch (error) {
      console.error('Recording error:', error);
      toast.error('Mikrofon erişimi reddedildi');
    }
  };

  // Stop recording
  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      toast.success('Kayıt tamamlandı! ✓');
    }
  };

  // Play recorded audio
  const playRecordedAudio = () => {
    if (recordedAudio && playbackRef.current) {
      const audioUrl = URL.createObjectURL(recordedAudio);
      playbackRef.current.src = audioUrl;
      playbackRef.current.play();
    }
  };

  // Save recording to server
  const saveRecordingToServer = async () => {
    if (!recordedAudio || !currentExercise) {
      toast.error("Kaydedilecek ses yok");
      return;
    }
    
    try {
      // Convert blob to base64
      const reader = new FileReader();
      reader.readAsDataURL(recordedAudio);
      reader.onloadend = async () => {
        const base64Audio = reader.result;
        
        try {
          const response = await axios.post(
            `${API_URL}/api/skill-tests/speaking/save-recording`,
            {
              level: "VOICE_PRACTICE",
              question_id: currentExercise.id,
              question_text: currentExercise.title + ": " + currentExercise.text.substring(0, 100),
              audio_data: base64Audio,
              duration_seconds: 0
            },
            { withCredentials: true }
          );
          
          if (response.data.success) {
            toast.success("Kayıt başarıyla kaydedildi! 🎉");
          }
        } catch (err) {
          console.error("Save recording error:", err);
          toast.error("Kayıt kaydedilemedi");
        }
      };
    } catch (err) {
      console.error("Recording save error:", err);
      toast.error("Kayıt işlemi başarısız");
    }
  };

  // Get AI feedback on pronunciation (simulated)
  const getFeedback = async () => {
    if (!recordedAudio || !currentExercise) return;
    
    setLoading(true);
    setFeedback(null);
    
    // Simulate AI feedback (in production, this would send audio to backend for analysis)
    try {
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Generate simulated feedback
      const scores = {
        pronunciation: Math.floor(Math.random() * 20) + 75,
        clarity: Math.floor(Math.random() * 20) + 70,
        pace: Math.floor(Math.random() * 20) + 72,
        confidence: Math.floor(Math.random() * 20) + 68
      };
      
      const overall = Math.round((scores.pronunciation + scores.clarity + scores.pace + scores.confidence) / 4);
      
      const feedbackTexts = {
        excellent: [
          "Mükemmel telaffuz! Profesyonel bir kabin memuru gibi konuştunuz.",
          "Harika! Sesiniz çok net ve anlaşılır.",
          "Çok iyi! Tempo ve tonlama doğru."
        ],
        good: [
          "İyi iş! Bazı kelimelere daha fazla dikkat edebilirsiniz.",
          "Güzel çalışma! Biraz daha yavaş konuşmayı deneyin.",
          "Başarılı! 'th' seslerine dikkat edin."
        ],
        needsWork: [
          "Gelişime açık. Referans sesi tekrar dinleyin.",
          "Pratik yapmaya devam edin. Tempoya dikkat edin.",
          "Daha yavaş ve net konuşmayı deneyin."
        ]
      };
      
      let feedbackCategory = overall >= 85 ? 'excellent' : overall >= 70 ? 'good' : 'needsWork';
      let randomFeedback = feedbackTexts[feedbackCategory][Math.floor(Math.random() * 3)];
      
      setFeedback({
        scores,
        overall,
        text: randomFeedback,
        tips: currentExercise.tips
      });
      
      if (overall >= 70 && !completedExercises.includes(currentExercise.id)) {
        setCompletedExercises([...completedExercises, currentExercise.id]);
        toast.success('Egzersiz tamamlandı! +10 Mil 🎉');
      }
      
    } catch (error) {
      console.error('Feedback error:', error);
      toast.error('Geri bildirim alınamadı');
    } finally {
      setLoading(false);
    }
  };

  // Reset current exercise
  const resetExercise = () => {
    setRecordedAudio(null);
    setFeedback(null);
  };

  // Category selection screen
  if (!selectedCategory) {
    return (
        <div className="min-h-screen bg-gray-50">
          <audio ref={audioRef} onEnded={() => setIsPlaying(false)} />
          
          {/* Header */}
          <div className="bg-gradient-to-r from-pink-500 to-rose-500 pb-8 rounded-b-[2rem]">
            <div className="max-w-4xl mx-auto px-4 pt-4">
              <button
                onClick={() => navigate('/english-training')}
                className="flex items-center gap-2 text-white/80 hover:text-white mb-4 transition-colors"
              >
                <ChevronLeft className="w-5 h-5" />
                <span className="font-medium">Geri</span>
              </button>
              
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center shadow-lg">
                  <Mic className="w-8 h-8 text-pink-500" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-white">Sesli Pratik</h1>
                  <p className="text-pink-100 text-sm">Anons okuma ve telaffuz değerlendirmesi</p>
                </div>
              </div>
            </div>
          </div>

          <div className="max-w-4xl mx-auto px-4 py-6 -mt-4">
            {/* Instructions */}
            <Card className="mb-6 border-0 shadow-lg bg-gradient-to-r from-pink-50 to-rose-50">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-pink-500 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Headphones className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-pink-800 mb-2">Nasıl Çalışır?</h3>
                  <ol className="text-pink-700 text-sm space-y-1">
                    <li>1. Önce referans sesi dinleyin</li>
                    <li>2. Metni yüksek sesle okuyun ve kaydedin</li>
                    <li>3. AI değerlendirmesi alın</li>
                    <li>4. Geri bildirimlere göre pratik yapın</li>
                  </ol>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Category Selection */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {VOICE_EXERCISES.map((category) => {
              const isLocked = isDemo && !demoAllowedCategories.includes(category.id);
              return (
              <Card 
                key={category.id}
                className={`transition-all border-2 border-transparent ${
                  isLocked 
                    ? 'opacity-50 cursor-not-allowed' 
                    : 'cursor-pointer hover:shadow-xl hover:-translate-y-1 hover:border-pink-300'
                }`}
                onClick={() => !isLocked && setSelectedCategory(category.id)}
              >
                <CardContent className="p-6 relative">
                  {isLocked && (
                    <div className="absolute top-2 right-2 bg-gray-800 text-white text-xs px-2 py-1 rounded-full">
                      🔒 Premium
                    </div>
                  )}
                  <div className="flex items-center gap-4 mb-4">
                    <div className={`w-16 h-16 bg-gradient-to-br ${isLocked ? 'from-gray-400 to-gray-500' : category.color} rounded-xl flex items-center justify-center`}>
                      <span className="text-3xl">{category.icon}</span>
                    </div>
                    <div>
                      <h3 className={`text-xl font-bold ${isLocked ? 'text-gray-500' : 'text-gray-800'}`}>{category.category}</h3>
                      <p className={`text-sm ${isLocked ? 'text-gray-400' : 'text-gray-500'}`}>
                        {isLocked ? 'Tam sürümde mevcut' : `${category.exercises.length} Egzersiz`}
                      </p>
                    </div>
                  </div>
                  {!isLocked && (
                    <div className="flex gap-2">
                      {category.exercises.map((ex, idx) => (
                        <Badge 
                          key={idx}
                          className={completedExercises.includes(ex.id) 
                            ? 'bg-green-500 text-white' 
                            : 'bg-gray-200 text-gray-600'
                          }
                        >
                          {completedExercises.includes(ex.id) ? '✓' : idx + 1}
                        </Badge>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
              );
            })}
          </div>
        </div>
      </div>
      
    );
  }

  // Exercise selection screen
  if (!currentExercise) {
    return (
      
        <div className="min-h-screen bg-gradient-to-br from-pink-50 via-rose-50 to-red-50 p-4">
          <audio ref={audioRef} onEnded={() => setIsPlaying(false)} />
          
          <div className="container mx-auto max-w-4xl">
            <div className="flex items-center gap-4 mb-8">
              <Button variant="ghost" onClick={() => setSelectedCategory(null)}>
                <ChevronLeft className="w-5 h-5 mr-1" />
                Geri
              </Button>
              <div>
                <h1 className="text-2xl font-black text-gray-800 flex items-center gap-3">
                  <span className="text-3xl">{currentCategory?.icon}</span>
                  {currentCategory?.category}
                </h1>
              </div>
            </div>

          <div className="grid gap-4">
            {currentCategory?.exercises.map((exercise) => (
              <Card 
                key={exercise.id}
                className="cursor-pointer hover:shadow-xl transition-all border-2 border-transparent hover:border-pink-300"
                onClick={() => setCurrentExercise(exercise)}
              >
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                        completedExercises.includes(exercise.id)
                          ? 'bg-green-500'
                          : `bg-gradient-to-br ${currentCategory.color}`
                      }`}>
                        {completedExercises.includes(exercise.id) 
                          ? <CheckCircle className="w-6 h-6 text-white" />
                          : <Mic className="w-6 h-6 text-white" />
                        }
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-gray-800">{exercise.title}</h3>
                        <p className="text-sm text-gray-500 line-clamp-1">{exercise.text.substring(0, 60)}...</p>
                      </div>
                    </div>
                    <Badge className={
                      exercise.difficulty === 'easy' ? 'bg-green-500 text-white' :
                      exercise.difficulty === 'medium' ? 'bg-amber-500 text-white' :
                      'bg-red-500 text-white'
                    }>
                      {exercise.difficulty === 'easy' ? 'Kolay' : 
                       exercise.difficulty === 'medium' ? 'Orta' : 'Zor'}
                    </Badge>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
      
    );
  }

  // Practice screen
  return (
    
      <div className="min-h-screen bg-gradient-to-br from-pink-50 via-rose-50 to-red-50 p-4">
        <audio ref={audioRef} onEnded={() => setIsPlaying(false)} />
        <audio ref={playbackRef} />
        
        <div className="container mx-auto max-w-2xl">
          <div className="flex items-center justify-between mb-6">
            <Button variant="ghost" onClick={() => { setCurrentExercise(null); resetExercise(); }}>
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <div className="text-center">
              <p className="text-sm text-gray-500">{currentCategory?.category}</p>
              <p className="font-bold">{currentExercise.title}</p>
            </div>
          <Badge className={`bg-gradient-to-r ${currentCategory?.color} text-white`}>
            {currentCategory?.icon}
          </Badge>
        </div>

        {/* Reference Text Card */}
        <Card className="border-2 border-pink-200 shadow-lg mb-6">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-gray-700">📝 Okuyacağınız Metin</h3>
              <Button
                size="sm"
                variant="outline"
                onClick={() => playReferenceAudio(currentExercise.text)}
                disabled={isPlaying}
                className="gap-2"
              >
                {isPlaying ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Volume2 className="w-4 h-4" />
                )}
                Dinle
              </Button>
            </div>
            
            <p className="text-lg text-gray-800 leading-relaxed mb-4">
              {currentExercise.text}
            </p>
            
            <div className="bg-gray-50 rounded-xl p-4">
              <p className="text-sm text-gray-600">
                <span className="font-medium">Türkçe: </span>
                {currentExercise.text_tr}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Recording Section */}
        <Card className="border-2 border-pink-300 shadow-lg mb-6">
          <CardContent className="p-6">
            <h3 className="font-bold text-gray-700 mb-4 text-center">🎙️ Sesinizi Kaydedin</h3>
            
            <div className="flex justify-center gap-4 mb-6">
              {!isRecording ? (
                <Button
                  onClick={startRecording}
                  className="w-20 h-20 rounded-full bg-gradient-to-br from-pink-500 to-rose-600 hover:scale-110 transition-transform"
                >
                  <Mic className="w-8 h-8" />
                </Button>
              ) : (
                <Button
                  onClick={stopRecording}
                  className="w-20 h-20 rounded-full bg-gradient-to-br from-red-500 to-red-700 animate-pulse hover:scale-110 transition-transform"
                >
                  <MicOff className="w-8 h-8" />
                </Button>
              )}
            </div>
            
            <p className="text-center text-sm text-gray-500 mb-4">
              {isRecording ? '🔴 Kayıt devam ediyor... Durdurmak için tıklayın' : 'Kaydetmek için mikrofona tıklayın'}
            </p>
            
            {recordedAudio && (
              <div className="flex justify-center gap-3">
                <Button
                  variant="outline"
                  onClick={playRecordedAudio}
                  className="gap-2"
                >
                  <Play className="w-4 h-4" />
                  Kaydı Dinle
                </Button>
                <Button
                  variant="outline"
                  onClick={resetExercise}
                  className="gap-2"
                >
                  <RotateCcw className="w-4 h-4" />
                  Tekrar Kaydet
                </Button>
                <Button
                  variant="outline"
                  onClick={saveRecordingToServer}
                  className="gap-2 border-green-500 text-green-600 hover:bg-green-50"
                >
                  <CheckCircle className="w-4 h-4" />
                  Kaydı Kaydet
                </Button>
                <Button
                  onClick={getFeedback}
                  disabled={loading}
                  className="gap-2 bg-gradient-to-r from-pink-500 to-rose-600"
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Sparkles className="w-4 h-4" />
                  )}
                  Değerlendir
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Feedback Section */}
        {feedback && (
          <Card className="border-2 border-green-300 shadow-lg mb-6 bg-gradient-to-br from-green-50 to-emerald-50">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-green-400 to-emerald-500 rounded-xl flex items-center justify-center">
                  <Trophy className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-800">Değerlendirme Sonucu</h3>
                  <p className="text-3xl font-black text-green-600">%{feedback.overall}</p>
                </div>
              </div>
              
              {/* Score breakdown */}
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="bg-white rounded-xl p-3 text-center">
                  <p className="text-xs text-gray-500">Telaffuz</p>
                  <p className="text-xl font-bold text-blue-600">%{feedback.scores.pronunciation}</p>
                </div>
                <div className="bg-white rounded-xl p-3 text-center">
                  <p className="text-xs text-gray-500">Netlik</p>
                  <p className="text-xl font-bold text-purple-600">%{feedback.scores.clarity}</p>
                </div>
                <div className="bg-white rounded-xl p-3 text-center">
                  <p className="text-xs text-gray-500">Tempo</p>
                  <p className="text-xl font-bold text-amber-600">%{feedback.scores.pace}</p>
                </div>
                <div className="bg-white rounded-xl p-3 text-center">
                  <p className="text-xs text-gray-500">Güven</p>
                  <p className="text-xl font-bold text-green-600">%{feedback.scores.confidence}</p>
                </div>
              </div>
              
              {/* Feedback text */}
              <div className="bg-white rounded-xl p-4 mb-4">
                <p className="text-gray-700">{feedback.text}</p>
              </div>
              
              {/* Tips */}
              <div className="bg-amber-50 rounded-xl p-4">
                <p className="text-sm font-bold text-amber-800 mb-2">💡 İpuçları:</p>
                <ul className="text-sm text-amber-700 space-y-1">
                  {feedback.tips.map((tip, idx) => (
                    <li key={idx}>• {tip}</li>
                  ))}
                </ul>
              </div>
            </CardContent>
          </Card>
        )}


        {/* Previous Recordings Section */}
        {previousRecordings.length > 0 && (
          <Card className="border border-purple-200 shadow-lg mb-6 bg-gradient-to-br from-purple-50 to-indigo-50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-bold text-purple-800 flex items-center gap-2">
                  <Headphones className="w-5 h-5" />
                  Onceki Kayitlarim ({previousRecordings.length})
                </h4>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowRecordings(!showRecordings)}
                >
                  {showRecordings ? "Gizle" : "Goster"}
                </Button>
              </div>
              {showRecordings && (
                <div className="space-y-3 max-h-60 overflow-y-auto">
                  {previousRecordings.slice(0, 10).map((rec, idx) => (
                    <div key={rec.id || idx} className="bg-white rounded-lg p-3 flex items-center justify-between">
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-800">{rec.exercise_title || rec.question_text || "Kayit"}</p>
                        <p className="text-xs text-gray-500">{new Date(rec.created_at).toLocaleDateString("tr-TR")}</p>
                      </div>
                      {rec.score && <Badge className="bg-green-100 text-green-800">%{rec.score}</Badge>}
                      {rec.audio_data && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            const audio = new Audio(rec.audio_data);
                            audio.play();
                          }}
                        >
                          <Play className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Tips Card */}
        <Card className="border border-gray-200">
          <CardContent className="p-4">
            <h4 className="font-bold text-gray-700 mb-2">📌 Bu Anons İçin İpuçları</h4>
            <ul className="text-sm text-gray-600 space-y-1">
              {currentExercise.tips.map((tip, idx) => (
                <li key={idx} className="flex items-start gap-2">
                  <Star className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                  <span>{tip}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      </div>
    </div>
    
  );
}
